package org.teddapp.project.core

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

/**
 * L'instantané de projection tel que le noyau le rend. L'application ne recalcule rien :
 * les heures, les paliers, le taux de décision et la cohérence viennent d'ici.
 *
 * Les noms de champs suivent le noyau, en français : c'est lui qui fait foi.
 */
@Serializable
data class CoreSnapshot(
    val competences: Map<String, CoreCompetence> = emptyMap(),
    val finance: CoreFinance = CoreFinance(),
    val revisions: Map<String, CoreCarte> = emptyMap(),
    val parcours: Map<String, CoreParcours> = emptyMap(),
    val coherence: List<CoreCoherence> = emptyList(),
)

/** Où en est le parcours d'une compétence, d'après le noyau. */
@Serializable
data class CoreParcours(
    @SerialName("competence_id") val competenceId: String = "",
    val titre: String = "",
    val etapes: List<CoreParcoursEtape> = emptyList(),
    @SerialName("etape_courante") val etapeCourante: String? = null,
    @SerialName("prochaine_feuille") val prochaineFeuille: String? = null,
    @SerialName("palier_atteint") val palierAtteint: String? = null,
    @SerialName("minutes_prevues") val minutesPrevues: Long = 0,
    @SerialName("minutes_faites") val minutesFaites: Long = 0,
    @SerialName("secondes_focus") val secondesFocus: Long = 0,
    val pourcentage: Int = 0,
)

@Serializable
data class CoreParcoursEtape(
    val code: String = "",
    val titre: String = "",
    val genre: String = "module",
    val palier: String? = null,
    @SerialName("minutes_prevues") val minutesPrevues: Long = 0,
    @SerialName("minutes_faites") val minutesFaites: Long = 0,
    @SerialName("feuilles_faites") val feuillesFaites: Long = 0,
    @SerialName("feuilles_total") val feuillesTotal: Long = 0,
    @SerialName("secondes_focus") val secondesFocus: Long = 0,
    val terminee: Boolean = false,
)

/** Où en est une carte de révision, d'après le noyau. */
@Serializable
data class CoreCarte(
    val id: String = "",
    @SerialName("competence_id") val competenceId: String = "",
    val case: Int = 1,
    @SerialName("intervalle_jours") val intervalleJours: Int = 1,
    @SerialName("derniere_revision_ms") val derniereRevisionMs: Long = 0,
    @SerialName("prochaine_revision_ms") val prochaineRevisionMs: Long = 0,
    val revisions: Long = 0,
    val oublis: Long = 0,
)

@Serializable
data class CoreCompetence(
    val id: String = "",
    val nom: String = "",
    val categorie: String = "",
    @SerialName("temps_cumule_secondes") val secondes: Long = 0,
    val sessions: Long = 0,
    /** Cible d'heures que le noyau connaît ; null tant qu'aucune n'a été déclarée. */
    @SerialName("heures_cible") val heuresCible: Long? = null,
    val palier: String = "DEBUTANT",
    @SerialName("palier_suivant") val palierSuivant: String? = null,
    @SerialName("heures_avant_palier_suivant") val heuresAvantSuivant: Long? = null,
    @SerialName("auto_explications") val explications: List<String> = emptyList(),
) {
    val heures: Double get() = secondes / 3600.0
}

@Serializable
data class CoreFinance(
    @SerialName("total_depense") val totalDepense: Double = 0.0,
    @SerialName("total_decisions") val totalDecisions: Int = 0,
    @SerialName("decisions_rationnelles") val decisionsRationnelles: Int = 0,
    @SerialName("depenses_par_categorie") val parCategorie: Map<String, Double> = emptyMap(),
) {
    /** Taux de décisions rationnelles, ou null tant qu'aucune décision n'a été prise. */
    val taux: Int?
        get() = if (totalDecisions == 0) null else (decisionsRationnelles * 100.0 / totalDecisions).toInt()
}

@Serializable
data class CoreCoherence(
    @SerialName("competence_id") val competenceId: String = "",
    @SerialName("competence_nom") val competenceNom: String = "",
    @SerialName("temps_heures") val heures: Double = 0.0,
    @SerialName("budget_depense") val budget: Double = 0.0,
    @SerialName("ratio_coherence") val ratio: Double = 0.0,
    @SerialName("alerte_illusion") val alerte: Boolean = false,
)

/** Lecteur tolérant : un champ inconnu ou manquant ne fait pas échouer la lecture. */
internal val coreJson: Json = Json {
    ignoreUnknownKeys = true
    isLenient = true
    coerceInputValues = true
}

/** Lit un instantané, ou null si le JSON n'a pas la forme attendue. */
fun parseCoreSnapshot(json: String): CoreSnapshot? =
    runCatching { coreJson.decodeFromString<CoreSnapshot>(json) }.getOrNull()
